import type { ReactNode } from 'react';
import { LogtoProvider, UserScope, useLogto, type LogtoConfig } from '@logto/react';
import { getConfig } from '@/config';
import { STAFF_ORGANIZATION_SCOPES } from '@/config/staffScopes';
import { StaffSignOutContext, type StaffSignOut } from './staffSignOut';

/**
 * The console's Logout lives outside the identity provider's hooks, and on a stage without an application
 * there is no provider at all. This hands it a sign-out it can call either way.
 */
function StaffSignOutBridge({ children }: { children: ReactNode }) {
  const { isAuthenticated, signOut } = useLogto();
  const staffSignOut: StaffSignOut = isAuthenticated ? () => signOut(`${window.location.origin}/`) : null;
  return <StaffSignOutContext.Provider value={staffSignOut}>{children}</StaffSignOutContext.Provider>;
}

/**
 * Mounts the identity provider the staff console signs in through. Logto is identity only — what a
 * token may touch is decided by the internal API, not here.
 *
 * A stage with no application registered renders its children untouched, so the Google sign-in the
 * console shipped with keeps working until every stage has one.
 */
export function StaffAuthProvider({ children }: { children: ReactNode }) {
  const { logtoEndpoint, logtoAppId } = getConfig();
  if (!logtoAppId) {
    return <>{children}</>;
  }

  const config: LogtoConfig = {
    endpoint: logtoEndpoint,
    appId: logtoAppId,
    scopes: [
      UserScope.Email,
      UserScope.Profile,
      UserScope.Organizations,
      UserScope.OrganizationRoles,
      // Organization scopes have to be requested here, or the organization token arrives with an
      // empty `scope` claim and the API can see which organization the caller acts for but not
      // what they may do in it.
      ...STAFF_ORGANIZATION_SCOPES,
    ],
  };

  return (
    <LogtoProvider config={config}>
      <StaffSignOutBridge>{children}</StaffSignOutBridge>
    </LogtoProvider>
  );
}
